/**
 * Build styles
 */
require('./index.css').toString();
import { v4 as uuidv4 } from 'uuid';

/**
 * @class Warning
 * @classdesc Component Review for Editor.js
 * @property {WarningData} data - Component Review`s input and output data
 * @property {object} api - Editor.js API instance
 *
 * @typedef {object} WarningData
 * @description Component Review`s input and output data
 * @property {string} title - warning`s title
 * @property {string} message - warning`s message
 *
 * @typedef {object} WarningConfig
 * @description Component Review`s initial configuration
 * @property {string} titlePlaceholder - placeholder to show in warning`s title input
 * @property {string} messagePlaceholder - placeholder to show in warning`s message input
 */
export default class Warning {

    /**
     * Notify core that read-only mode is supported
     */
    static get isReadOnlySupported() {
        return true;
    }

    /**
     * Get Toolbox settings
     *
     * @public
     * @returns {string}
     */
    static get toolbox() {
        return {
            icon: '<svg height="18" width="18" aria-hidden="true" focusable="false" data-prefix="fal" data-icon="clipboard-check" class="svg-inline--fa fa-clipboard-check fa-w-12" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path fill="currentColor" d="M336 64h-88.6c.4-2.6.6-5.3.6-8 0-30.9-25.1-56-56-56s-56 25.1-56 56c0 2.7.2 5.4.6 8H48C21.5 64 0 85.5 0 112v352c0 26.5 21.5 48 48 48h288c26.5 0 48-21.5 48-48V112c0-26.5-21.5-48-48-48zM192 32c13.3 0 24 10.7 24 24s-10.7 24-24 24-24-10.7-24-24 10.7-24 24-24zm160 432c0 8.8-7.2 16-16 16H48c-8.8 0-16-7.2-16-16V112c0-8.8 7.2-16 16-16h48v20c0 6.6 5.4 12 12 12h168c6.6 0 12-5.4 12-12V96h48c8.8 0 16 7.2 16 16v352zm-58.9-236.4c-4.7-4.7-12.3-4.7-17-.1L158.4 344.3 108 293.7c-4.7-4.7-12.3-4.7-17 0l-8.5 8.5c-4.7 4.7-4.7 12.3 0 17l67.4 67.6c4.7 4.7 12.3 4.7 17 0l134.8-133.7c4.7-4.7 4.7-12.3.1-17l-8.7-8.5z"></path></svg>',
            title: 'Component Review'
        };
    }

    /**
     * Allow to press Enter inside the Warning
     *
     * @public
     * @returns {boolean}
     */
    static get enableLineBreaks() {
        return true;
    }

    /**
     * Default placeholder for warning title
     *
     * @public
     * @returns {string}
     */
    static get DEFAULT_TITLE_PLACEHOLDER() {
        return 'Question';
    }

    /**
     * Default placeholder for warning message
     *
     * @public
     * @returns {string}
     */
    static get DEFAULT_MESSAGE_PLACEHOLDER() {
        return 'Description & Help';
    }

    /**
     * Component Review`s styles
     *
     * @returns {object}
     */
    get CSS() {
        return {
            baseClass: this.api.styles.block,
            wrapper: 'cdx-warning',
            title: 'cdx-warning__title',
            input: this.api.styles.input,
            message: 'cdx-warning__message',
        };
    }

    /**
     * Render plugin`s main Element and fill it with saved data
     *
     * @param {WarningData} data — previously saved data
     * @param {WarningConfig} config — user config for Tool
     * @param {object} api - Editor.js API
     * @param {boolean} readOnly - read-only mode flag
     */
    constructor({ data, config, api, readOnly }) {
        this.api = api;
        this.readOnly = readOnly;

        this.titlePlaceholder = config.titlePlaceholder || Warning.DEFAULT_TITLE_PLACEHOLDER;
        this.messagePlaceholder = config.messagePlaceholder || Warning.DEFAULT_MESSAGE_PLACEHOLDER;

        this.data = {
            title: data.title || '',
            message: data.message || '',
            id: data.id || uuidv4()
        };
    }

    /**
     * Create Component Review container with inputs
     *
     * @returns {Element}
     */
    render() {
        const container = this._make('div', [this.CSS.baseClass, this.CSS.wrapper]);
        const title = this._make('div', [this.CSS.input, this.CSS.title], {
            innerHTML: '<strong>Component Review</strong><br><em>Completed components (lessons and quizzes) taken during a course will be presented including statistics, quiz results, etc. for review. This is generally used to allow the user confirm on what they completed.</em>',
        });

        container.appendChild(title);

        return container;
    }

    /**
     * Extract Warning data from Component Review element
     *
     * @param {HTMLDivElement} warningElement - element to save
     * @returns {WarningData}
     */
    save(warningElement) {
        const title = warningElement.querySelector(`.${this.CSS.title}`);
        const message = warningElement.querySelector(`.${this.CSS.message}`);
        const fType = warningElement.querySelector(`.field-type`);

        return Object.assign(this.data, {

        });
    }

    /**
     * Helper for making Elements with attributes
     *
     * @param  {string} tagName           - new Element tag name
     * @param  {Array|string} classNames  - list or name of CSS classname(s)
     * @param  {object} attributes        - any attributes
     * @returns {Element}
     */
    _make(tagName, classNames = null, attributes = {}) {
        const el = document.createElement(tagName);

        if (Array.isArray(classNames)) {
            el.classList.add(...classNames);
        } else if (classNames) {
            el.classList.add(classNames);
        }

        for (const attrName in attributes) {
            el[attrName] = attributes[attrName];
        }

        return el;
    }

    /**
     * Sanitizer config for Component Review saved data
     *
     * @returns {object}
     */
    static get sanitize() {
        return {
            title: {},
            message: {},
        };
    }
}
